import { useUserQuery } from 'api/user/queries'
import { countryToLanguagesMap } from 'consts/countryToLanguageMap'
import { useContentCountry } from 'hooks/useContentCountry'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { usePreferredLanguage } from 'hooks/usePreferredLanguage'

import { Language, Country } from 'types/Api'
import { rollbar } from 'utils/rollbar'

type LanguageOption = {
  language: Language
  country: Country
}

export type OnSelectOption = ({ language, country }: LanguageOption) => void

export const useLanguageAndCountrySelector = () => {
  const hasMultiLanguagesFlag = useFeatureFlag('multi_languages')
  const { data } = useUserQuery({
    select: ({ user: { country, availableLanguages } }) => ({ country, availableLanguages }),
  })

  const { contentCountry, onSelectContentCountry } = useContentCountry()
  const { onSetPreferredLanguage } = usePreferredLanguage()

  const languageOptions: LanguageOption[] = []

  if (hasMultiLanguagesFlag && data?.country === 'dk') {
    languageOptions.push(
      { language: 'da', country: 'dk' },
      { language: 'no', country: 'dk' },
      { language: 'nn', country: 'dk' },
      { language: 'no', country: 'no' },
      { language: 'nn', country: 'no' },
    )
  }
  else if (data?.availableLanguages) {
    languageOptions.push(
      ...data.availableLanguages.map(lang => ({
        language: lang,
        country: data.country,
      })),
    )
  }

  const handleSelectOption: OnSelectOption = ({ language, country: selectedCountry }) => {
    if (!data?.country) return

    const targetCountry = selectedCountry || contentCountry

    if (!countryToLanguagesMap[targetCountry].includes(language)) {
      rollbar.warning(`Attempted to change language to unsupported language: ${language} for country ${targetCountry}`)
      return
    }

    if (selectedCountry && selectedCountry !== data.country) {
      onSelectContentCountry(selectedCountry)
    }
    else if (!selectedCountry || selectedCountry === data.country) {
      onSelectContentCountry(null)
    }
    onSetPreferredLanguage(language)
  }

  return {
    onSelectLanguage: handleSelectOption,
    languageOptions,
    currentCountry: contentCountry,
  }
}
